/**
 * Phase 1 Settings shell — single page with shadcn Tabs for all sub-modules.
 * Profile · Tenant · Brand Profile · Users · Roles · API Keys · Feature Flags · Audit Log
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { api, auth } from "../../lib/api";
import { useLocale, useT } from "../../lib/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter, DialogClose,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Copy, RotateCcw, Trash2, UserPlus, Plus, KeyRound, History } from "lucide-react";
import DiagnosticsPanel from "../../components/app/DiagnosticsPanel";

export default function Settings() {
  const t = useT();
  const [tab, setTab] = useState(
    new URLSearchParams(window.location.search).get("tab") || "profile"
  );
  return (
    <div data-testid="settings-page" className="space-y-6">
      <h1 className="text-3xl font-semibold tracking-tight">{t("settings.title")}</h1>
      <div className="flex gap-2 text-sm">
        <a data-testid="settings-budget-link" href="/app/settings/budget"
           className="underline text-primary">Budgetverwaltung</a>
        <span className="text-muted-foreground">·</span>
        <a href="/app/settings/legal"
           className="underline text-primary">Rechtliches</a>
      </div>
      <Tabs value={tab} onValueChange={(v) => {
        setTab(v);
        window.history.replaceState(null, "", `?tab=${v}`);
      }}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-profile">{t("settings.profile")}</TabsTrigger>
          <TabsTrigger value="tenant" data-testid="tab-tenant">{t("settings.tenant")}</TabsTrigger>
          <TabsTrigger value="brand" data-testid="tab-brand">{t("settings.brand")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">{t("settings.users")}</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">{t("settings.roles")}</TabsTrigger>
          <TabsTrigger value="api-keys" data-testid="tab-api-keys">{t("settings.api_keys")}</TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags">{t("settings.flags")}</TabsTrigger>
          <TabsTrigger value="diagnostics" data-testid="tab-diagnostics">Diagnostics</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">{t("settings.audit")}</TabsTrigger>
        </TabsList>
        <div className="mt-6">
          <TabsContent value="profile"><ProfileTab /></TabsContent>
          <TabsContent value="tenant"><TenantTab /></TabsContent>
          <TabsContent value="brand"><BrandTab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="roles"><RolesTab /></TabsContent>
          <TabsContent value="api-keys"><ApiKeysTab /></TabsContent>
          <TabsContent value="flags"><FlagsTab /></TabsContent>
          <TabsContent value="diagnostics"><DiagnosticsPanel /></TabsContent>
          <TabsContent value="audit"><AuditTab /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

/* ---------- helpers ---------- */
function useAsync(fn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fn());
    } catch (e) {
      // Phase 6 fix — fresh tenants have no records for many resources
      // (api-keys, brand-profile, flags, users beyond the owner…).
      // A missing/empty resource must NOT freeze the tab on
      // "Loading…" forever. Surface the failure so callers can render
      // an empty state instead.
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, error, reload };
}

function L({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}

/* ---------- Profile ---------- */
function ProfileTab() {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const [user, setUser] = useState(auth.getUser() || {});
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    api.get("/users/me").then((r) => { setUser(r.data); auth.setSession({ user: r.data }); }).catch(() => {});
    api.get("/sessions").then((r) => setSessions(r.data.items)).catch(() => {});
  }, []);
  const save = async () => {
    try {
      const r = await api.put(`/users/${user.id}`, {
        full_name: user.full_name, display_name: user.display_name,
        locale: user.locale, timezone: user.timezone,
      });
      setUser(r.data);
      auth.setSession({ user: r.data });
      toast.success(t("settings.saved"));
    } catch (e) { toast.error("Save failed"); }
  };
  const revoke = async (sid) => {
    try {
      await api.delete(`/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.id !== sid));
      toast.success("Session revoked");
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">{t("settings.profile")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <L label={t("auth.email")}>
              <Input readOnly value={user.email || ""} data-testid="profile-email" />
            </L>
            <L label={t("auth.full_name")}>
              <Input value={user.full_name || ""} data-testid="profile-full-name"
                     onChange={(e) => setUser({ ...user, full_name: e.target.value })} />
            </L>
            <L label="Display name">
              <Input value={user.display_name || ""} data-testid="profile-display-name"
                     onChange={(e) => setUser({ ...user, display_name: e.target.value })} />
            </L>
            <L label="Role"><Input readOnly value={user.role || ""} /></L>
            <L label={t("settings.language")}>
              <Select value={locale} onValueChange={(v) => { setLocale(v); setUser({ ...user, locale: v }); }}>
                <SelectTrigger data-testid="profile-locale"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="de">Deutsch (DE)</SelectItem>
                  <SelectItem value="en">English (EN)</SelectItem>
                </SelectContent>
              </Select>
            </L>
            <L label="Timezone">
              <Input value={user.timezone || "Europe/Berlin"} data-testid="profile-timezone"
                     onChange={(e) => setUser({ ...user, timezone: e.target.value })} />
            </L>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={save} data-testid="profile-save">{t("settings.save")}</Button>
            {/* Phase 18c — "Show setup banner again" affordance. Fires POST
                /onboarding/reset-banner (owner-scoped via tenancy.write).
                Silently no-ops for non-owners (403 caught below). */}
            <Button
              variant="ghost" size="sm"
              data-testid="profile-reset-onboarding-banner"
              onClick={async () => {
                try {
                  await api.post("/onboarding/reset-banner");
                  toast.success(locale === "de"
                    ? "Setup-Banner wird wieder angezeigt."
                    : "Setup banner is visible again.");
                } catch (e) {
                  toast.error(locale === "de"
                    ? "Konnte den Banner nicht zurücksetzen."
                    : "Couldn't reset the banner.");
                }
              }}
            >
              {locale === "de"
                ? "Setup-Banner wieder anzeigen"
                : "Show setup banner again"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t("settings.sessions")}</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Device / UA</TableHead><TableHead>IP</TableHead>
              <TableHead>Created</TableHead><TableHead>Last seen</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sessions.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">No sessions.</TableCell></TableRow>
              )}
              {sessions.map((s) => (
                <TableRow key={s.id} data-testid="session-row">
                  <TableCell className="text-xs truncate max-w-[300px]">{s.user_agent || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{s.ip || "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDate(s.created_at)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(s.last_seen_at)}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={() => revoke(s.id)} data-testid={`revoke-session-${s.id}`}>{t("settings.revoke")}</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------- Tenant ---------- */
function TenantTab() {
  const t = useT();
  const [tenant, setTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    api.get("/tenants/me").then((r) => setTenant(r.data));
    api.get("/plans").then((r) => setPlans(r.data.items));
    api.get("/tenants/me/usage").then((r) => setUsage(r.data));
  }, []);
  const save = async () => {
    try {
      const { id, ...patch } = tenant;
      const r = await api.put("/tenants/me", patch);
      setTenant(r.data); toast.success(t("settings.saved"));
    } catch (e) { toast.error("Save failed"); }
  };
  if (!tenant) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const plan = plans.find((p) => p.key === tenant.plan_key) || {};
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">{t("settings.tenant")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <L label="Name"><Input value={tenant.name || ""} data-testid="tenant-name"
              onChange={(e) => setTenant({ ...tenant, name: e.target.value })} /></L>
            <L label="Legal name"><Input value={tenant.legal_name || ""} data-testid="tenant-legal-name"
              onChange={(e) => setTenant({ ...tenant, legal_name: e.target.value })} /></L>
            <L label="Slug"><Input readOnly value={tenant.slug || ""} className="font-mono" /></L>
            <L label="Billing email"><Input value={tenant.billing_email || ""} data-testid="tenant-billing"
              onChange={(e) => setTenant({ ...tenant, billing_email: e.target.value })} /></L>
            <L label="Industry"><Input value={tenant.industry || ""}
              onChange={(e) => setTenant({ ...tenant, industry: e.target.value })} /></L>
            <L label="Website"><Input value={tenant.website || ""}
              onChange={(e) => setTenant({ ...tenant, website: e.target.value })} /></L>
            <L label="VAT id"><Input value={tenant.vat_id || ""}
              onChange={(e) => setTenant({ ...tenant, vat_id: e.target.value })} /></L>
            <L label="Default locale">
              <Select value={tenant.default_locale} onValueChange={(v) => setTenant({ ...tenant, default_locale: v })}>
                <SelectTrigger data-testid="tenant-locale"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="de">Deutsch</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </L>
            <L label="Timezone"><Input value={tenant.timezone || ""}
              onChange={(e) => setTenant({ ...tenant, timezone: e.target.value })} /></L>
            <L label="Plan">
              <Select value={tenant.plan_key} onValueChange={(v) => setTenant({ ...tenant, plan_key: v })}>
                <SelectTrigger data-testid="tenant-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => <SelectItem key={p.key} value={p.key}>{p.name} · €{p.price_monthly}/mo</SelectItem>)}
                </SelectContent>
              </Select>
            </L>
          </div>
          <div className="border-t pt-4">
            <div className="text-sm font-medium mb-2">Address</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <L label="Street"><Input value={tenant.address?.street || ""}
                onChange={(e) => setTenant({ ...tenant, address: { ...tenant.address, street: e.target.value } })} /></L>
              <L label="ZIP"><Input value={tenant.address?.zip || ""}
                onChange={(e) => setTenant({ ...tenant, address: { ...tenant.address, zip: e.target.value } })} /></L>
              <L label="City"><Input value={tenant.address?.city || ""}
                onChange={(e) => setTenant({ ...tenant, address: { ...tenant.address, city: e.target.value } })} /></L>
              <L label="Country"><Input value={tenant.address?.country || "DE"}
                onChange={(e) => setTenant({ ...tenant, address: { ...tenant.address, country: e.target.value } })} /></L>
            </div>
          </div>
          <Button onClick={save} data-testid="tenant-save">{t("settings.save")}</Button>
        </CardContent>
      </Card>

      <Card data-testid="tenant-policy-card">
        <CardHeader><CardTitle className="text-base">Approval policy</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <L label="Budget change — threshold %">
              <Input type="number" min="0" max="100"
                     data-testid="policy-pct"
                     value={tenant.approval_budget_change_threshold_pct ?? ""}
                     placeholder="default: 20"
                     onChange={(e) => setTenant({
                       ...tenant,
                       approval_budget_change_threshold_pct: e.target.value === "" ? null : Number(e.target.value),
                     })} />
            </L>
            <L label="Budget change — threshold € (abs)">
              <Input type="number" min="0"
                     data-testid="policy-abs"
                     value={tenant.approval_budget_change_threshold_abs ?? ""}
                     placeholder="default: 500"
                     onChange={(e) => setTenant({
                       ...tenant,
                       approval_budget_change_threshold_abs: e.target.value === "" ? null : Number(e.target.value),
                     })} />
            </L>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-accent/30"
                   data-testid="policy-campaign-publish">
              <input type="checkbox"
                     className="mt-1"
                     checked={tenant.approval_required_for_campaign_publish ?? true}
                     onChange={(e) => setTenant({
                       ...tenant,
                       approval_required_for_campaign_publish: e.target.checked,
                     })} />
              <div>
                <div className="text-sm font-medium">Require approval to publish campaigns</div>
                <div className="text-xs text-muted-foreground">Submit-for-review opens a publish approval the owner must grant.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-accent/30"
                   data-testid="policy-asset-publish">
              <input type="checkbox"
                     className="mt-1"
                     checked={tenant.approval_required_for_asset_publish ?? false}
                     onChange={(e) => setTenant({
                       ...tenant,
                       approval_required_for_asset_publish: e.target.checked,
                     })} />
              <div>
                <div className="text-sm font-medium">Require approval to publish assets</div>
                <div className="text-xs text-muted-foreground">Reserved — enforced once asset publication has a publication flow.</div>
              </div>
            </label>
          </div>
          <Button onClick={save} data-testid="policy-save">{t("settings.save")}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t("settings.plan_usage")}</CardTitle></CardHeader>
        <CardContent>
          {usage && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Kpi label="AI runs" value={usage.ai_runs_total} />
              <Kpi label="Users" value={`${usage.users_active} / ${plan.limits?.users ?? "—"}`} />
              <Kpi label="Reports" value={usage.research_reports} />
              <Kpi label="Cost (USD)" value={`$${usage.cost_usd_total}`} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
function Kpi({ label, value }) {
  return <div className="border rounded-md p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-2xl font-semibold mt-1">{value}</div>
  </div>;
}

/* ---------- Brand Profile ---------- */
function BrandTab() {
  const t = useT();
  const { data: bp, loading, error, reload } = useAsync(async () => (await api.get("/brand-profile")).data);
  const [draft, setDraft] = useState(null);
  const [activeSub, setActiveSub] = useState("identity");
  useEffect(() => {
    // Phase 6 fix — fresh tenants may not yet have a brand profile
    // document. Seed a blank draft so the editor renders (all fields
    // empty) instead of hanging on "Loading…".
    if (bp) setDraft(bp);
    else if (!loading) setDraft({ version: 1 });
  }, [bp, loading]);

  if (loading || !draft) return <div data-testid="brand-loading" className="text-sm text-muted-foreground">Loading…</div>;
  if (error && !bp) {
    // silent fallback to blank draft — no toast (empty tenant is
    // expected state, not an error)
  }

  const save = async () => {
    try {
      const r = await api.put("/brand-profile", { ...draft, change_summary: `edit_${activeSub}` });
      setDraft(r.data); toast.success(t("settings.saved"));
    } catch (e) { toast.error("Save failed"); }
  };

  const upd = (k, v) => setDraft({ ...draft, [k]: v });
  const updNest = (k, sub, v) => setDraft({ ...draft, [k]: { ...(draft[k] || {}), [sub]: v } });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("settings.brand")}</CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="brand-version">v{draft.version || 1}</Badge>
          <Button size="sm" onClick={save} data-testid="brand-save">{t("settings.save")}</Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeSub} onValueChange={setActiveSub}>
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="identity">Identity</TabsTrigger>
            <TabsTrigger value="voice">Voice & Style</TabsTrigger>
            <TabsTrigger value="personas">Personas</TabsTrigger>
            <TabsTrigger value="catalog">Products & Services</TabsTrigger>
            <TabsTrigger value="regions">Regions</TabsTrigger>
            <TabsTrigger value="design">Design</TabsTrigger>
            <TabsTrigger value="legal">Legal</TabsTrigger>
            <TabsTrigger value="objectives">Objectives</TabsTrigger>
            <TabsTrigger value="pmm" data-testid="tab-brand-pmm">Product Marketing</TabsTrigger>
            <TabsTrigger value="history"><History className="size-3 mr-1" />History</TabsTrigger>
          </TabsList>
          <div className="mt-4 space-y-4">
            <TabsContent value="identity" className="space-y-3">
              <L label="Mission"><Textarea data-testid="brand-mission" rows={2} value={draft.mission || ""} onChange={(e) => upd("mission", e.target.value)} /></L>
              <L label="Vision"><Textarea rows={2} value={draft.vision || ""} onChange={(e) => upd("vision", e.target.value)} /></L>
              <L label="Values (comma-separated)"><Input value={(draft.values || []).join(", ")} onChange={(e) => upd("values", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></L>
              <L label="Brand guidelines"><Textarea rows={5} value={draft.brand_guidelines || ""} onChange={(e) => upd("brand_guidelines", e.target.value)} /></L>
            </TabsContent>
            <TabsContent value="voice" className="space-y-3">
              <L label="Tone of voice (comma-separated)"><Input value={(draft.tone_of_voice || []).join(", ")} onChange={(e) => upd("tone_of_voice", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></L>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <L label="Sentence length"><Input value={draft.writing_style?.sentence_length || ""} onChange={(e) => updNest("writing_style", "sentence_length", e.target.value)} /></L>
                <L label="Vocabulary level"><Input value={draft.writing_style?.vocabulary_level || ""} onChange={(e) => updNest("writing_style", "vocabulary_level", e.target.value)} /></L>
                <L label="Perspective"><Input value={draft.writing_style?.perspective || ""} onChange={(e) => updNest("writing_style", "perspective", e.target.value)} /></L>
              </div>
            </TabsContent>
            <TabsContent value="personas">
              <ItemListEditor items={draft.buyer_personas || []} onChange={(v) => upd("buyer_personas", v)}
                fields={["name", "role", "age_range", "geography"]} label="persona" />
            </TabsContent>
            <TabsContent value="catalog">
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-medium mb-2">Products</div>
                  <ItemListEditor items={draft.products || []} onChange={(v) => upd("products", v)}
                    fields={["name", "category", "price_range", "usp"]} label="product" />
                </div>
                <div>
                  <div className="text-sm font-medium mb-2">Services</div>
                  <ItemListEditor items={draft.services || []} onChange={(v) => upd("services", v)}
                    fields={["name", "category"]} label="service" />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="regions">
              <ItemListEditor items={draft.regions || []} onChange={(v) => upd("regions", v)}
                fields={["name", "country", "type", "notes"]} label="region" />
            </TabsContent>
            <TabsContent value="design">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <L label="Primary color"><Input value={draft.design?.primary_color || ""} onChange={(e) => updNest("design", "primary_color", e.target.value)} /></L>
                <L label="Secondary color"><Input value={draft.design?.secondary_color || ""} onChange={(e) => updNest("design", "secondary_color", e.target.value)} /></L>
                <L label="Accent color"><Input value={draft.design?.accent_color || ""} onChange={(e) => updNest("design", "accent_color", e.target.value)} /></L>
                <L label="Font family"><Input value={draft.design?.font_family || ""} onChange={(e) => updNest("design", "font_family", e.target.value)} /></L>
                <L label="Logo URL"><Input value={draft.design?.logo_url || ""} onChange={(e) => updNest("design", "logo_url", e.target.value)} /></L>
                <L label="Favicon URL"><Input value={draft.design?.favicon_url || ""} onChange={(e) => updNest("design", "favicon_url", e.target.value)} /></L>
              </div>
            </TabsContent>
            <TabsContent value="legal" className="space-y-3">
              <L label="Legal restrictions (one per line)"><Textarea rows={4} value={(draft.legal_restrictions || []).join("\n")} onChange={(e) => upd("legal_restrictions", e.target.value.split("\n").filter(Boolean))} /></L>
              <L label="Compliance rules (one per line)"><Textarea rows={4} value={(draft.compliance_rules || []).join("\n")} onChange={(e) => upd("compliance_rules", e.target.value.split("\n").filter(Boolean))} /></L>
            </TabsContent>
            <TabsContent value="objectives" className="space-y-3">
              <L label="Marketing objectives (one per line)"><Textarea rows={6} value={(draft.marketing_objectives || []).join("\n")} onChange={(e) => upd("marketing_objectives", e.target.value.split("\n").filter(Boolean))} /></L>
            </TabsContent>
            <TabsContent value="pmm">
              <ProductMarketingContextPanel />
            </TabsContent>
            <TabsContent value="history"><BrandHistory onRestored={reload} /></TabsContent>
          </div>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function BrandHistory({ onRestored }) {
  const { data, loading, reload } = useAsync(async () => (await api.get("/brand-profile/history")).data);
  const [restoring, setRestoring] = useState(null);
  if (loading) return <div data-testid="brand-history-loading" className="text-sm text-muted-foreground">Loading…</div>;
  const items = data?.items || [];
  if (!items.length) return <div className="text-sm text-muted-foreground">No history yet.</div>;
  const restore = async (v) => {
    if (!window.confirm(`Restore version ${v}? This creates a new version (${v}+1) with the restored content.`)) return;
    setRestoring(v);
    try {
      await api.post(`/brand-profile/restore/${v}`);
      toast.success(`Restored v${v}`);
      reload(); onRestored?.();
    } catch (e) { toast.error("Restore failed"); }
    finally { setRestoring(null); }
  };
  return (
    <Table>
      <TableHeader><TableRow>
        <TableHead>Version</TableHead><TableHead>Updated at</TableHead>
        <TableHead>Updated by</TableHead><TableHead>Change</TableHead><TableHead></TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {items.map((h) => (
          <TableRow key={h.version} data-testid={`brand-history-${h.version}`}>
            <TableCell className="font-mono">v{h.version}</TableCell>
            <TableCell className="text-xs">{fmtDate(h.updated_at || h.created_at)}</TableCell>
            <TableCell className="text-xs font-mono">{(h.updated_by || "—").slice(0, 8)}</TableCell>
            <TableCell className="text-xs">{h.change_summary || "—"}</TableCell>
            <TableCell>
              <Button size="sm" variant="outline" onClick={() => restore(h.version)} disabled={restoring === h.version}
                data-testid={`brand-restore-${h.version}`}>
                <RotateCcw className="size-3 mr-1" /> Restore
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/* ---------- Wave 1: Product Marketing Context (coreyhaines skill) ---------- */
export function ProductMarketingContextPanel() {
  const { data: initial, loading, reload } = useAsync(async () =>
    (await api.get("/brand-profile/product-marketing")).data);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  useEffect(() => { if (initial) setDraft(initial); }, [initial]);

  if (loading || !draft) {
    return <div className="text-sm text-muted-foreground"
                      data-testid="pmm-loading">Loading…</div>;
  }

  const upd = (k, v) => setDraft({ ...draft, [k]: v });

  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        "/brand-profile/product-marketing/generate", {});
      setDraft(r.data);
      if (r.data.fallback_reason) {
        toast.warning(`KI-Vorschlag nicht verfügbar (${r.data.fallback_reason})`);
      } else {
        toast.success("Product-Marketing-Kontext neu generiert");
      }
    } catch (e) {
      toast.error("Regenerate failed");
    } finally {
      setBusy(false);
    }
  };

  const saveManual = async () => {
    setBusy(true);
    try {
      const { last_source, last_generated_at, last_skill_invocation_id,
                 fallback_reason, invocation_id, latency_ms, ...rest } = draft;
      const r = await api.put(
        "/brand-profile/product-marketing", rest);
      setDraft(r.data);
      setManualMode(false);
      toast.success("Manuell gespeichert");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const asList = (v) => Array.isArray(v) ? v.join("\n") : "";
  const fromList = (s) => (s || "").split("\n").map((x) => x.trim())
                                                                        .filter(Boolean);

  return (
    <div data-testid="pmm-panel" className="space-y-4">
      <div className="flex items-center justify-between border rounded-md p-3 bg-muted/20">
        <div className="text-xs">
          <div data-testid="pmm-last-source">
            Quelle: <strong>{draft.last_source || "manual"}</strong>
            {draft.last_generated_at && (
              <> · Zuletzt: {new Date(draft.last_generated_at).toLocaleString("de-DE")}</>
            )}
          </div>
          {draft.fallback_reason && (
            <div className="mt-1 text-amber-700 dark:text-amber-400"
                    data-testid="pmm-fallback-banner">
              Fallback aktiv: {draft.fallback_reason}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={regenerate}
                    disabled={busy} data-testid="pmm-regenerate-btn">
            Neu via KI generieren
          </Button>
          {!manualMode ? (
            <Button size="sm" onClick={() => setManualMode(true)}
                      disabled={busy} data-testid="pmm-edit-btn">
              Manuell bearbeiten
            </Button>
          ) : (
            <>
              <Button size="sm" variant="ghost"
                        onClick={() => { setDraft(initial); setManualMode(false); }}
                        disabled={busy} data-testid="pmm-cancel-btn">
                Abbrechen
              </Button>
              <Button size="sm" onClick={saveManual} disabled={busy}
                        data-testid="pmm-save-btn">
                Speichern
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4"
              data-testid="pmm-fields">
        {[
          ["audience", "Audience", "textarea", 3],
          ["positioning", "Positioning", "textarea", 3],
          ["icp", "ICP (Ideal Customer Profile)", "textarea", 3],
          ["category", "Category", "input"],
          ["differentiation", "Differentiation", "textarea", 3],
          ["tone", "Tone", "input"],
          ["brand_voice", "Brand voice", "textarea", 3],
        ].map(([k, label, kind, rows]) => (
          <div key={k}>
            <Label className="text-xs">{label}</Label>
            {kind === "input" ? (
              <Input
                value={String(draft[k] || "")}
                onChange={(e) => upd(k, e.target.value)}
                disabled={!manualMode}
                data-testid={`pmm-field-${k}`}
              />
            ) : (
              <Textarea
                rows={rows || 3}
                value={String(draft[k] || "")}
                onChange={(e) => upd(k, e.target.value)}
                disabled={!manualMode}
                data-testid={`pmm-field-${k}`}
              />
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          ["value_props", "Value props (one per line)"],
          ["key_messages", "Key messages (one per line)"],
          ["proof_points", "Proof points (one per line)"],
        ].map(([k, label]) => (
          <div key={k}>
            <Label className="text-xs">{label}</Label>
            <Textarea rows={5} value={asList(draft[k])}
                          onChange={(e) => upd(k, fromList(e.target.value))}
                          disabled={!manualMode}
                          data-testid={`pmm-list-${k}`} />
          </div>
        ))}
      </div>
    </div>
  );
}


function ItemListEditor({ items, onChange, fields, label }) {
  const t = useT();
  const update = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, fields.reduce((acc, f) => ({ ...acc, [f]: "" }), {})]);
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border rounded-md p-3 flex gap-3 items-start">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
            {fields.map((f) => (
              <div key={f}>
                <Label className="text-xs">{f}</Label>
                <Input value={String(it[f] ?? "")} onChange={(e) => update(i, f, e.target.value)} />
              </div>
            ))}
          </div>
          <Button variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`remove-${label}-${i}`}><Trash2 className="size-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`add-${label}`}>
        <Plus className="size-3 mr-1" />Add {label}
      </Button>
    </div>
  );
}

/* ---------- Users ---------- */
function UsersTab() {
  const t = useT();
  const { data, loading, reload } = useAsync(async () => (await api.get("/users")).data);
  const { data: rolesData } = useAsync(async () => (await api.get("/roles")).data);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("manager");
  const [inviteUrl, setInviteUrl] = useState(null);

  const invite = async () => {
    try {
      const r = await api.post("/users/invite", { email, role });
      const fullUrl = `${window.location.origin}${r.data.accept_url}`;
      setInviteUrl(fullUrl);
      toast.success("Invite created");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleStatus = async (u) => {
    try {
      const action = u.status === "active" ? "disable" : "enable";
      await api.post(`/users/${u.id}/${action}`);
      toast.success(`User ${action}d`);
      reload();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return <div data-testid="users-loading" className="text-sm text-muted-foreground">Loading…</div>;
  const rows = data?.items || [];
  // Phase 6 fix — empty state must reflect "no invitations yet", so
  // don't count the current owner themselves. The owner is always
  // present in the response and would otherwise mask the empty state.
  const currentUserId = auth.getUser()?.id;
  const invitees = rows.filter((u) => u.id !== currentUserId);
  const showEmpty = invitees.length === 0;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("settings.users")}</CardTitle>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setInviteUrl(null); }}>
          <DialogTrigger asChild><Button data-testid="invite-user-btn"><UserPlus className="size-4 mr-1" />Invite user</Button></DialogTrigger>
          <DialogContent data-testid="invite-dialog">
            <DialogHeader><DialogTitle>Invite a user</DialogTitle></DialogHeader>
            {!inviteUrl ? (
              <div className="space-y-3">
                <L label="Email"><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" /></L>
                <L label="Role">
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(rolesData?.items || []).map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </L>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">Email delivery isn't wired yet. Share this link with the invitee:</div>
                <div className="border rounded-md p-2 bg-muted/30 break-all text-xs font-mono" data-testid="invite-url">{inviteUrl}</div>
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success("Copied"); }}>
                  <Copy className="size-4 mr-1" />Copy URL
                </Button>
              </div>
            )}
            <DialogFooter>
              {!inviteUrl ? <Button onClick={invite} data-testid="invite-submit">Send invite</Button>
                          : <DialogClose asChild><Button>Done</Button></DialogClose>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Email</TableHead><TableHead>Name</TableHead>
            <TableHead>Role</TableHead><TableHead>Status</TableHead>
            <TableHead>Last login</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {showEmpty && (
              <TableRow data-testid="users-empty">
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  Noch keine weiteren Nutzer eingeladen. Lade jemanden per „Invite user" ein.
                </TableCell>
              </TableRow>
            )}
            {rows.map((u) => (
              <TableRow key={u.id} data-testid="user-row">
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.full_name || "—"}</TableCell>
                <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                <TableCell><Badge variant={u.status === "active" ? "default" : "secondary"}>{u.status}</Badge></TableCell>
                <TableCell className="text-xs">{fmtDate(u.last_login_at)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => toggleStatus(u)} data-testid={`toggle-user-${u.id}`}>
                    {u.status === "active" ? "Disable" : "Enable"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/* ---------- Roles ---------- */
function RolesTab() {
  const { data, loading } = useAsync(async () => (await api.get("/roles")).data);
  if (loading) return <div data-testid="roles-loading" className="text-sm text-muted-foreground">Loading…</div>;
  const items = data?.items || [];
  // Phase 6 fix — the response mixes built-in global roles (owner,
  // admin, analyst…) with tenant-scoped custom roles. The empty-state
  // is about "no CUSTOM roles yet"; the built-ins are surfaced
  // separately as a read-only reference block so the operator sees
  // what's already there.
  const custom = items.filter((r) => !r.built_in && r.tenant_id);
  const builtIns = items.filter((r) => r.built_in);
  return (
    <div className="space-y-4">
      {custom.length === 0 && (
        <div data-testid="roles-empty" className="text-sm text-muted-foreground rounded-md border p-4">
          Noch keine eigenen Rollen konfiguriert. Der Tenant nutzt bislang nur die eingebauten Standardrollen.
        </div>
      )}
      {builtIns.length > 0 && (
        <div className="rounded-md border p-4 space-y-2" data-testid="roles-builtins">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Standard-Rollen (eingebaut, nicht editierbar)
          </div>
          <div className="flex flex-wrap gap-2">
            {builtIns.map((r) => (
              <Badge key={r.id || r.key} variant="secondary"
                       data-testid={`role-builtin-${r.key}`}>
                {r.name || r.key}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {custom.map((r) => (
        <Card key={r.key} data-testid={`role-${r.key}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2">
              {r.name}
              <code className="text-xs px-1.5 py-0.5 rounded bg-muted font-mono">{r.key}</code>
              {r.built_in && <Badge variant="secondary" className="text-xs">built-in</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-2">{r.description}</p>
            <div className="flex flex-wrap gap-1.5">
              {(r.permissions || []).map((p, i) => <Badge key={i} variant="outline" className="font-mono text-xs">{p}</Badge>)}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ---------- API Keys ---------- */
function ApiKeysTab() {
  const t = useT();
  const { data, loading, reload } = useAsync(async () => (await api.get("/api-keys")).data);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [plaintext, setPlaintext] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const create = async () => {
    try {
      const r = await api.post("/api-keys", { name, scopes: ["*"] });
      setPlaintext(r.data.key);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? Any clients using it will lose access immediately.")) return;
    try { await api.delete(`/api-keys/${id}`); toast.success("Revoked"); reload(); }
    catch (e) { toast.error("Failed"); }
  };
  if (loading) return <div data-testid="api-keys-loading" className="text-sm text-muted-foreground">Loading…</div>;
  const rows = data?.items || [];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("settings.api_keys")}</CardTitle>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setPlaintext(null); setConfirmed(false); setName(""); } }}>
          <DialogTrigger asChild><Button data-testid="create-api-key-btn"><KeyRound className="size-4 mr-1" />Create key</Button></DialogTrigger>
          <DialogContent data-testid="api-key-dialog">
            <DialogHeader><DialogTitle>{plaintext ? "Save this key now" : "Create API key"}</DialogTitle></DialogHeader>
            {!plaintext ? (
              <div className="space-y-3">
                <L label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="api-key-name" placeholder="e.g. CI integration" /></L>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-amber-600 dark:text-amber-400">This is the only time the plaintext key is shown. Save it securely.</div>
                <div className="border rounded-md p-2 bg-muted/30 break-all text-xs font-mono" data-testid="api-key-plaintext">{plaintext}</div>
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(plaintext); toast.success("Copied"); }}>
                  <Copy className="size-4 mr-1" />Copy
                </Button>
                <label className="flex items-center gap-2 text-sm pt-2">
                  <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="api-key-confirmed" />
                  I have saved this key
                </label>
              </div>
            )}
            <DialogFooter>
              {!plaintext ? <Button onClick={create} disabled={!name} data-testid="api-key-submit">Create</Button>
                          : <DialogClose asChild><Button disabled={!confirmed}>Done</Button></DialogClose>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Prefix</TableHead>
            <TableHead>Scopes</TableHead><TableHead>Last used</TableHead>
            <TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow data-testid="api-keys-empty">
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  Noch keine API-Schlüssel angelegt. Erstelle einen über „Create key".
                </TableCell>
              </TableRow>
            )}
            {rows.map((k) => (
              <TableRow key={k.id} data-testid="api-key-row">
                <TableCell>{k.name}</TableCell>
                <TableCell className="font-mono text-xs">{k.key_prefix}</TableCell>
                <TableCell className="text-xs">{(k.scopes || []).join(", ") || "—"}</TableCell>
                <TableCell className="text-xs">{fmtDate(k.last_used_at)}</TableCell>
                <TableCell><Badge variant={k.revoked ? "secondary" : "default"}>{k.revoked ? "revoked" : "active"}</Badge></TableCell>
                <TableCell>{!k.revoked && <Button variant="ghost" size="sm" onClick={() => revoke(k.id)} data-testid={`revoke-key-${k.id}`}>Revoke</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/* ---------- Feature Flags ---------- */
function FlagsTab() {
  const t = useT();
  const { data, loading, reload } = useAsync(async () => (await api.get("/feature-flags")).data);
  const toggle = async (flag) => {
    try { await api.put(`/feature-flags/${flag.key}`, { enabled: !flag.enabled }); reload(); }
    catch (e) { toast.error("Failed"); }
  };
  if (loading) return <div data-testid="flags-loading" className="text-sm text-muted-foreground">Loading…</div>;
  const flags = data?.flags || [];
  // Phase 6 fix — the list mixes global platform defaults and
  // tenant-scoped overrides. "No feature flags" for the operator
  // means "no tenant-scoped OVERRIDES yet" — surface the globals as
  // read-only reference so the operator sees what's active.
  const overrides = flags.filter((f) => f.scope === "tenant" || f.scope === "role");
  const globals   = flags.filter((f) => f.scope !== "tenant" && f.scope !== "role");
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t("settings.flags")}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {overrides.length === 0 && (
          <div data-testid="flags-empty" className="text-sm text-muted-foreground rounded-md border p-4">
            Für diesen Tenant sind keine spezifischen Overrides konfiguriert. Es gelten die Standard-Flags der Plattform.
          </div>
        )}
        {globals.length > 0 && (
          <div className="rounded-md border p-3 space-y-2" data-testid="flags-globals">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Aktive Standard-Flags (plattformweit)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {globals.map((f) => (
                <Badge
                  key={f.key}
                  variant={f.enabled ? "default" : "outline"}
                  className="font-mono text-xs"
                  data-testid={`flag-global-${f.key}`}
                >
                  {f.key}{f.enabled ? "" : " (aus)"}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {overrides.map((f) => (
          <div key={f.key} data-testid={`flag-${f.key}`} className="flex items-center justify-between border rounded-md p-3 gap-3">
            <div>
              <div className="font-medium">{f.key}</div>
              <div className="text-xs text-muted-foreground">{f.description || "—"}</div>
            </div>
            <Switch checked={!!f.enabled} onCheckedChange={() => toggle(f)} data-testid={`flag-toggle-${f.key}`} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* ---------- Audit Log ---------- */
function AuditTab() {
  const t = useT();
  const [filters, setFilters] = useState({
    action: "", resource_type: "", correlation_id: "",
    from_ts: "", to_ts: "", actor: "",
  });
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.items || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const clean = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v && v.length > 0)
    );
    // backend expects from_ts/to_ts ISO; expand date pickers (YYYY-MM-DD) to full day
    if (clean.from_ts) clean.from_ts = `${clean.from_ts}T00:00:00Z`;
    if (clean.to_ts) clean.to_ts = `${clean.to_ts}T23:59:59Z`;
    // Actor filter: map "api_key" → actor regex; "user:<id>" → exact
    if (clean.actor) {
      if (clean.actor === "api_key") clean.actor_type = "api_key";
      else clean.actor_type = "user";
      delete clean.actor;
    }
    const params = { page, limit: 25, ...clean };
    const r = await api.get("/audit/events", { params });
    setItems(r.data.items || []);
    setTotal(r.data.total || 0);
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t("settings.audit")}</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <L label="Action"><Input data-testid="audit-filter-action" value={filters.action} onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(1); }} placeholder="e.g. identity.user.invited" /></L>
          <L label="Resource type"><Input data-testid="audit-filter-resource" value={filters.resource_type} onChange={(e) => { setFilters({ ...filters, resource_type: e.target.value }); setPage(1); }} /></L>
          <L label="Correlation id"><Input data-testid="audit-filter-correlation" value={filters.correlation_id} onChange={(e) => { setFilters({ ...filters, correlation_id: e.target.value }); setPage(1); }} className="font-mono" /></L>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <L label="From">
            <Input type="date" data-testid="audit-filter-from" value={filters.from_ts} onChange={(e) => { setFilters({ ...filters, from_ts: e.target.value }); setPage(1); }} />
          </L>
          <L label="To">
            <Input type="date" data-testid="audit-filter-to" value={filters.to_ts} onChange={(e) => { setFilters({ ...filters, to_ts: e.target.value }); setPage(1); }} />
          </L>
          <L label="Actor">
            <Select value={filters.actor || "all"} onValueChange={(v) => { setFilters({ ...filters, actor: v === "all" ? "" : v }); setPage(1); }}>
              <SelectTrigger data-testid="audit-filter-actor"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actors</SelectItem>
                <SelectItem value="api_key">API key</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={`user`}>{u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </L>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Time</TableHead><TableHead>Actor</TableHead>
            <TableHead>Action</TableHead><TableHead>Resource</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">No events.</TableCell></TableRow>
            )}
            {items.map((e) => (
              <TableRow key={e.id} data-testid="audit-row">
                <TableCell className="text-xs">{fmtDate(e.created_at || e.occurred_at)}</TableCell>
                <TableCell className="text-xs">{e.actor || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{e.action}</TableCell>
                <TableCell className="text-xs">{e.resource_type}{e.resource_id ? `:${e.resource_id.slice(0, 8)}…` : ""}</TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => setSelected(e)}>Details</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <div>Total: {total}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
            <div className="px-2 py-1">Page {page}</div>
            <Button variant="outline" size="sm" disabled={page * 25 >= total} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>

        <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
          <DialogContent data-testid="audit-detail-dialog" className="max-w-3xl">
            <DialogHeader><DialogTitle>Audit event</DialogTitle></DialogHeader>
            {selected && (
              <div className="space-y-3 text-sm">
                <div><b>Action:</b> <code>{selected.action}</code></div>
                <div><b>Actor:</b> {selected.actor}</div>
                <div><b>Resource:</b> {selected.resource_type} / <code className="text-xs">{selected.resource_id}</code></div>
                <div><b>Correlation:</b> <code className="text-xs font-mono">{selected.correlation_id}</code></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="font-medium text-xs uppercase tracking-wider mb-1">Before</div><pre className="text-xs bg-muted/30 rounded p-2 overflow-auto max-h-64">{JSON.stringify(selected.before || {}, null, 2)}</pre></div>
                  <div><div className="font-medium text-xs uppercase tracking-wider mb-1">After</div><pre className="text-xs bg-muted/30 rounded p-2 overflow-auto max-h-64">{JSON.stringify(selected.after || {}, null, 2)}</pre></div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

/* ---------- utils ---------- */
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch (e) { return String(s); }
}
